/**
 * ConversationViewer (MUI)
 *
 * 테넌트별 Claude 대화 세션을 조회/이어하기/이름변경/삭제한다.
 * teams-tab의 ConversationHistory + ConversationViewer(Fluent UI) 합본을 MUI로 재작성.
 *
 * 동작:
 *  - 좌측 목록: 페이지네이션(20개씩), 이름 인라인 편집, 삭제, 무한스크롤
 *  - 우측 상세: 메시지 목록 + "이어하기" 입력창
 *  - 상단 액션: 새 대화 시작, 다중 선택 복사, 새로고침
 */

import { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItemButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ChecklistIcon from "@mui/icons-material/Checklist";

import * as tenantsApi from "../api/tenants";
import type {
  ConversationSummary,
  ConversationMessage,
} from "../api/tenants";
import { copyToClipboard, formatTimestamp } from "../helpers/format";

const PAGE_SIZE = 20;

interface ConversationViewerProps {
  tenantCode: string;
}

function getDisplayName(conv: ConversationSummary): string {
  return (
    conv.name ||
    conv.firstMessage?.substring(0, 40) ||
    conv.convId.replace(/^teams-claude-[^-]+-/, "").substring(0, 30)
  );
}

export function ConversationViewer({ tenantCode }: ConversationViewerProps) {
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [listLoading, setListLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  const [selectedConvId, setSelectedConvId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");

  const [chatInput, setChatInput] = useState("");
  const [chatSending, setChatSending] = useState(false);
  const [continuedMessages, setContinuedMessages] = useState<
    { role: "user" | "assistant"; content: string }[]
  >([]);

  const [newDialogOpen, setNewDialogOpen] = useState(false);
  const [newConvMessage, setNewConvMessage] = useState("");
  const [newConvName, setNewConvName] = useState("");
  const [newConvSending, setNewConvSending] = useState(false);

  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [copyingMultiple, setCopyingMultiple] = useState(false);
  const [copiedSingle, setCopiedSingle] = useState(false);

  const [deletingId, setDeletingId] = useState<string | null>(null);

  const sidebarRef = useRef<HTMLDivElement>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);

  const loadList = useCallback(
    async (append: boolean, currentCount: number) => {
      if (append) setLoadingMore(true);
      else setListLoading(true);
      try {
        const offset = append ? currentCount : 0;
        const data = await tenantsApi.fetchConversationsPaginated(
          tenantCode,
          PAGE_SIZE,
          offset,
        );
        if (append) {
          setConversations((prev) => [...prev, ...data.conversations]);
        } else {
          setConversations(data.conversations);
        }
        setTotal(data.total);
        setListError(null);
      } catch (e) {
        if (!append) setConversations([]);
        setListError(
          e instanceof Error ? e.message : "대화 목록을 불러오지 못했습니다.",
        );
      } finally {
        if (append) setLoadingMore(false);
        else setListLoading(false);
      }
    },
    [tenantCode],
  );

  useEffect(() => {
    setConversations([]);
    setTotal(0);
    setSelectedConvId(null);
    setMessages([]);
    setContinuedMessages([]);
    setChatInput("");
    setSelectMode(false);
    setSelectedIds(new Set());
    loadList(false, 0);
    // loadList 의존성 제거 — tenantCode 변경 시 1회만 로드
  }, [tenantCode, loadList]);

  useEffect(() => {
    if (!selectedConvId) {
      setMessages([]);
      return;
    }
    setMessagesLoading(true);
    setContinuedMessages([]);
    setChatInput("");
    setCopiedSingle(false);
    tenantsApi
      .fetchConversationMessages(tenantCode, selectedConvId)
      .then((msgs) => setMessages(msgs))
      .catch(() => setMessages([]))
      .finally(() => setMessagesLoading(false));
  }, [tenantCode, selectedConvId]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [continuedMessages]);

  // 무한 스크롤
  useEffect(() => {
    const el = sidebarRef.current;
    const hasMore = conversations.length < total;
    if (!el || !hasMore) return;
    const onScroll = () => {
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 50 && !loadingMore) {
        loadList(true, conversations.length);
      }
    };
    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, [conversations.length, total, loadingMore, loadList]);

  const startEdit = (conv: ConversationSummary) => {
    setEditingId(conv.convId);
    setEditValue(getDisplayName(conv));
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditValue("");
  };

  const saveEdit = async (convId: string) => {
    const trimmed = editValue.trim();
    if (!trimmed) return cancelEdit();
    try {
      await tenantsApi.renameConversation(tenantCode, convId, trimmed);
      setConversations((prev) =>
        prev.map((c) => (c.convId === convId ? { ...c, name: trimmed } : c)),
      );
    } finally {
      cancelEdit();
    }
  };

  const handleDelete = async (convId: string) => {
    if (!window.confirm("이 대화를 삭제하시겠습니까?")) return;
    setDeletingId(convId);
    try {
      await tenantsApi.deleteConversation(tenantCode, convId);
      setConversations((prev) => prev.filter((c) => c.convId !== convId));
      setTotal((prev) => Math.max(0, prev - 1));
      if (selectedConvId === convId) {
        setSelectedConvId(null);
      }
    } catch (e) {
      setListError(e instanceof Error ? e.message : "삭제 실패");
    } finally {
      setDeletingId(null);
    }
  };

  const sendChat = async () => {
    if (!selectedConvId || !chatInput.trim() || chatSending) return;
    const msg = chatInput.trim();
    setChatInput("");
    setChatSending(true);
    setContinuedMessages((prev) => [...prev, { role: "user", content: msg }]);
    try {
      const data = await tenantsApi.continueConversation(
        tenantCode,
        selectedConvId,
        msg,
      );
      setContinuedMessages((prev) => [
        ...prev,
        { role: "assistant", content: data.response },
      ]);
    } catch (e) {
      setContinuedMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: `오류: ${e instanceof Error ? e.message : "전송 실패"}`,
        },
      ]);
    } finally {
      setChatSending(false);
    }
  };

  const startNewConv = async () => {
    if (!newConvMessage.trim() || newConvSending) return;
    setNewConvSending(true);
    try {
      await tenantsApi.startConversation(
        tenantCode,
        newConvMessage.trim(),
        newConvName.trim() || undefined,
      );
      setNewDialogOpen(false);
      setNewConvMessage("");
      setNewConvName("");
      setTimeout(() => loadList(false, 0), 1500);
    } catch (e) {
      alert(`새 대화 시작 실패: ${e instanceof Error ? e.message : "오류"}`);
    } finally {
      setNewConvSending(false);
    }
  };

  const toggleSelectMode = () => {
    setSelectMode((prev) => {
      if (prev) setSelectedIds(new Set());
      return !prev;
    });
  };

  const toggleOne = (convId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(convId)) next.delete(convId);
      else next.add(convId);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedIds.size === conversations.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(conversations.map((c) => c.convId)));
    }
  };

  const copySelected = async () => {
    if (selectedIds.size === 0) return;
    setCopyingMultiple(true);
    try {
      const convs = conversations.filter((c) => selectedIds.has(c.convId));
      const results = await Promise.allSettled(
        convs.map((c) => tenantsApi.fetchConversationMessages(tenantCode, c.convId)),
      );
      const blocks = results.map((r, i) => {
        const conv = convs[i];
        const header = `=== [${getDisplayName(conv)}] (${conv.messageCount}건) ===`;
        if (r.status === "fulfilled") {
          const body = r.value
            .map((m) => {
              const label = m.role === "user" ? "사용자" : "Claude";
              const time = new Date(m.timestamp).toLocaleString("ko-KR", {
                hour: "2-digit",
                minute: "2-digit",
              });
              return `[${label}] ${time}\n${m.content}`;
            })
            .join("\n\n");
          return `${header}\n\n${body}`;
        }
        return `${header}\n\n(메시지 로드 실패)`;
      });
      await copyToClipboard(blocks.join("\n\n"));
      setSelectMode(false);
      setSelectedIds(new Set());
    } finally {
      setCopyingMultiple(false);
    }
  };

  const copySingle = async () => {
    const allMsgs = [
      ...messages.map((m) => ({
        role: m.role,
        content: m.content,
        time: new Date(m.timestamp).toLocaleString("ko-KR", {
          hour: "2-digit",
          minute: "2-digit",
        }),
      })),
      ...continuedMessages.map((m) => ({ role: m.role, content: m.content, time: "" })),
    ];
    const text = allMsgs
      .map((m) => {
        const label = m.role === "user" ? "사용자" : "Claude";
        const t = m.time ? ` ${m.time}` : "";
        return `[${label}]${t}\n${m.content}`;
      })
      .join("\n\n");
    await copyToClipboard(text);
    setCopiedSingle(true);
    setTimeout(() => setCopiedSingle(false), 2000);
  };

  const hasMore = conversations.length < total;
  const allSelected =
    conversations.length > 0 && selectedIds.size === conversations.length;

  return (
    <Box sx={{ display: "flex", height: "100%", minHeight: 500, gap: 1 }}>
      {/* 좌측: 대화 목록 */}
      <Paper
        variant="outlined"
        sx={{ width: 320, display: "flex", flexDirection: "column" }}
      >
        <Box
          sx={{
            p: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Typography variant="subtitle2">
            대화 세션 {total > 0 && `(${conversations.length}/${total})`}
          </Typography>
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="선택 모드">
              <IconButton
                size="small"
                color={selectMode ? "primary" : "default"}
                onClick={toggleSelectMode}
              >
                <ChecklistIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="새 대화">
              <IconButton size="small" onClick={() => setNewDialogOpen(true)}>
                <AddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="새로고침">
              <IconButton size="small" onClick={() => loadList(false, 0)}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        {selectMode && conversations.length > 0 && (
          <Box
            sx={{
              px: 1.5,
              py: 0.5,
              display: "flex",
              alignItems: "center",
              gap: 1,
              borderBottom: 1,
              borderColor: "divider",
              bgcolor: "action.hover",
            }}
          >
            <Checkbox size="small" checked={allSelected} onChange={toggleAll} />
            <Typography variant="caption" sx={{ flex: 1 }}>
              전체선택 ({selectedIds.size})
            </Typography>
            <Button
              size="small"
              variant="contained"
              startIcon={
                copyingMultiple ? <CircularProgress size={12} /> : <ContentCopyIcon />
              }
              onClick={copySelected}
              disabled={selectedIds.size === 0 || copyingMultiple}
            >
              복사
            </Button>
          </Box>
        )}

        {listError && (
          <Alert severity="warning" sx={{ m: 1 }}>
            {listError}
          </Alert>
        )}

        <Box ref={sidebarRef} sx={{ flex: 1, overflow: "auto" }}>
          {listLoading && conversations.length === 0 ? (
            <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
              <CircularProgress size={20} />
            </Box>
          ) : conversations.length === 0 ? (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ p: 3, display: "block", textAlign: "center" }}
            >
              대화 내역이 없습니다
            </Typography>
          ) : (
            <List dense disablePadding>
              {conversations.map((conv) => {
                const isEditing = editingId === conv.convId;
                const isSelected = selectedConvId === conv.convId;
                const isChecked = selectedIds.has(conv.convId);
                return (
                  <ListItemButton
                    key={conv.convId}
                    selected={isSelected}
                    onClick={() => {
                      if (selectMode) toggleOne(conv.convId);
                      else if (!isEditing) setSelectedConvId(conv.convId);
                    }}
                    sx={{ alignItems: "flex-start", py: 1 }}
                  >
                    {selectMode && (
                      <Checkbox
                        size="small"
                        checked={isChecked}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => toggleOne(conv.convId)}
                        sx={{ mr: 0.5, p: 0.5 }}
                      />
                    )}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      {isEditing ? (
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <TextField
                            size="small"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") saveEdit(conv.convId);
                              if (e.key === "Escape") cancelEdit();
                            }}
                            autoFocus
                            sx={{ flex: 1 }}
                          />
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              saveEdit(conv.convId);
                            }}
                          >
                            <CheckIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              cancelEdit();
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      ) : (
                        <>
                          <Typography
                            variant="body2"
                            fontWeight={500}
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {getDisplayName(conv)}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block" }}
                          >
                            {conv.messageCount}건 · {formatTimestamp(conv.lastTimestamp || null)}
                          </Typography>
                        </>
                      )}
                    </Box>
                    {!selectMode && !isEditing && (
                      <Stack direction="row" spacing={0}>
                        <Tooltip title="이름 변경">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              startEdit(conv);
                            }}
                          >
                            <EditIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="삭제">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(conv.convId);
                            }}
                            disabled={deletingId === conv.convId}
                          >
                            {deletingId === conv.convId ? (
                              <CircularProgress size={12} />
                            ) : (
                              <DeleteIcon sx={{ fontSize: 14 }} />
                            )}
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    )}
                  </ListItemButton>
                );
              })}
              {loadingMore && (
                <Box sx={{ p: 1, display: "flex", justifyContent: "center" }}>
                  <CircularProgress size={16} />
                </Box>
              )}
              {hasMore && !loadingMore && (
                <Box sx={{ p: 1, textAlign: "center" }}>
                  <Button size="small" onClick={() => loadList(true, conversations.length)}>
                    더 불러오기
                  </Button>
                </Box>
              )}
            </List>
          )}
        </Box>
      </Paper>

      {/* 우측: 메시지 상세 */}
      <Paper
        variant="outlined"
        sx={{ flex: 1, display: "flex", flexDirection: "column" }}
      >
        {!selectedConvId ? (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              대화를 선택하세요
            </Typography>
          </Box>
        ) : (
          <>
            <Box
              sx={{
                p: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderBottom: 1,
                borderColor: "divider",
              }}
            >
              <Typography variant="subtitle2" sx={{ fontFamily: "monospace" }}>
                {selectedConvId}
              </Typography>
              <Button
                size="small"
                startIcon={<ContentCopyIcon />}
                onClick={copySingle}
                color={copiedSingle ? "success" : "inherit"}
                disabled={messages.length === 0 && continuedMessages.length === 0}
              >
                {copiedSingle ? "복사됨" : "복사"}
              </Button>
            </Box>

            <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
              {messagesLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <Stack spacing={1.5}>
                  {messages.map((m, i) => (
                    <MessageBubble
                      key={`m-${i}`}
                      role={m.role}
                      content={m.content}
                      time={m.timestamp ? formatTimestamp(m.timestamp) : undefined}
                    />
                  ))}
                  {continuedMessages.length > 0 && (
                    <Divider textAlign="center">
                      <Typography variant="caption" color="text.secondary">
                        이어하기
                      </Typography>
                    </Divider>
                  )}
                  {continuedMessages.map((m, i) => (
                    <MessageBubble key={`c-${i}`} role={m.role} content={m.content} />
                  ))}
                  <div ref={chatEndRef} />
                </Stack>
              )}
            </Box>

            <Box
              sx={{
                p: 1,
                borderTop: 1,
                borderColor: "divider",
                display: "flex",
                gap: 1,
              }}
            >
              <TextField
                size="small"
                fullWidth
                multiline
                maxRows={4}
                placeholder="메시지를 입력하고 Enter..."
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    sendChat();
                  }
                }}
                disabled={chatSending}
              />
              <Button
                variant="contained"
                onClick={sendChat}
                disabled={!chatInput.trim() || chatSending}
                startIcon={
                  chatSending ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    <SendIcon />
                  )
                }
              >
                보내기
              </Button>
            </Box>
          </>
        )}
      </Paper>

      {/* 새 대화 다이얼로그 */}
      <Dialog
        open={newDialogOpen}
        onClose={() => !newConvSending && setNewDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>새 대화 시작</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="대화 이름 (선택)"
              size="small"
              value={newConvName}
              onChange={(e) => setNewConvName(e.target.value)}
              disabled={newConvSending}
              fullWidth
            />
            <TextField
              label="메시지"
              required
              multiline
              minRows={4}
              value={newConvMessage}
              onChange={(e) => setNewConvMessage(e.target.value)}
              disabled={newConvSending}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewDialogOpen(false)} disabled={newConvSending}>
            취소
          </Button>
          <Button
            variant="contained"
            onClick={startNewConv}
            disabled={!newConvMessage.trim() || newConvSending}
            startIcon={newConvSending ? <CircularProgress size={14} /> : undefined}
          >
            {newConvSending ? "전송 중..." : "시작"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

interface MessageBubbleProps {
  role: "user" | "assistant";
  content: string;
  time?: string;
}

function MessageBubble({ role, content, time }: MessageBubbleProps) {
  const isUser = role === "user";
  return (
    <Box sx={{ display: "flex", justifyContent: isUser ? "flex-end" : "flex-start" }}>
      <Box
        sx={{
          maxWidth: "80%",
          p: 1.2,
          borderRadius: 1.5,
          bgcolor: isUser ? "primary.main" : "grey.100",
          color: isUser ? "primary.contrastText" : "text.primary",
        }}
      >
        <Typography
          variant="caption"
          sx={{ opacity: 0.7, display: "block", mb: 0.3 }}
        >
          {isUser ? "사용자" : "Claude"}
          {time && ` · ${time}`}
        </Typography>
        <Typography
          variant="body2"
          sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
        >
          {content}
        </Typography>
      </Box>
    </Box>
  );
}
